<template>
  <header class="site-header">
    <div class="header-inner">
      <router-link to="/" class="site-logo" aria-label="Home">
        <img
          v-if="siteConfig.logoUrl && !logoFailed"
          :src="siteConfig.logoUrl"
          :alt="siteConfig.siteName || '网站 Logo'"
          @error="logoFailed = true"
        >
        <span v-else class="brand-word site-logo__fallback">{{ siteConfig.siteName || '' }}</span>
      </router-link>

      <div class="header-desktop">
        <form class="header-search" role="search" @submit.prevent="search">
          <input
            v-model.trim="keyword"
            type="search"
            :placeholder="t('search')"
            :aria-label="t('search')"
          >
          <button
            type="submit"
            :title="t('search')"
            :aria-label="t('search')"
            :aria-busy="searching.toString()"
            :disabled="searching"
          >
            <svg class="search-icon" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="5.25" cy="5.25" r="4.42" />
              <path d="M8.55 8.55 11.17 11.17" />
            </svg>
          </button>
        </form>

        <nav class="header-nav" aria-label="Products">
          <div
            v-for="category in vehicleGroups"
            :key="category.id"
            class="vehicle-menu"
            @mouseenter="openVehicleMenu(category.id)"
            @mouseleave="closeVehicleMenu"
          >
            <button
              type="button"
              class="header-link"
              :aria-expanded="(vehicleOpenId === category.id).toString()"
              @click="toggleVehicleMenu(category.id)"
            >
              <span>{{ category.name }}</span>
              <img :class="['dropdown-icon', { 'dropdown-icon--open': vehicleOpenId === category.id }]" src="../tp/Frame.svg" alt="">
            </button>
            <div v-if="vehicleOpenId === category.id && (category.products || []).length" class="vehicle-dropdown">
              <router-link
                v-for="product in (category.products || [])"
                :key="product.id || product.name"
                :to="productLink(product)"
              >{{ product.name }}</router-link>
            </div>
          </div>
        </nav>
      </div>

      <div class="header-actions">
        <router-link class="header-service-link" to="/service-providers">{{ t('headerService') }}</router-link>
        <span class="header-service-divider" aria-hidden="true"></span>

        <div class="language-switcher" @mouseenter="openLanguageMenu" @mouseleave="closeLanguageMenu">
          <button
            type="button"
            class="language-button"
            :title="t('changeLanguage')"
            :aria-expanded="languageOpen.toString()"
            @click="toggleLanguageMenu"
          >
            <img class="globe-icon" src="../tp/Group 3.svg" alt="">
            <span>{{ language.toUpperCase() }}</span>
            <img :class="['dropdown-icon', { 'dropdown-icon--open': languageOpen }]" src="../tp/Frame.svg" alt="">
          </button>
          <div v-if="languageOpen" class="language-dropdown">
            <button v-for="item in languages" :key="item" type="button" @click="changeLanguage(item)">
              {{ item.toUpperCase() }}
            </button>
          </div>
        </div>

        <button
          type="button"
          class="menu-toggle"
          :title="mobileOpen ? t('closeMenu') : t('openMenu')"
          :aria-label="mobileOpen ? t('closeMenu') : t('openMenu')"
          :aria-expanded="mobileOpen.toString()"
          @click="mobileOpen = !mobileOpen"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>
    <MobileMenu :visible="mobileOpen" :categories="vehicleGroups" @close="mobileOpen = false" />
  </header>
</template>

<script>
import MobileMenu from './MobileMenu.vue'
import { getProducts, listData, sortByOrder } from '../api/portal'

export default {
  name: 'AppHeader',
  components: { MobileMenu },
  data () {
    return {
      keyword: '',
      vehicleOpenId: null,
      languageOpen: false,
      mobileOpen: false,
      logoFailed: false,
      searching: false,
      languages: ['en', 'fr', 'es']
    }
  },
  computed: {
    siteConfig () { return this.$store.state.siteConfig },
    categories () { return this.$store.state.categories },
    vehicleGroups () { return this.categories },
    language () { return this.$store.getters.language },
    t () { return this.$store.getters.t }
  },
  watch: {
    '$route.path' () {
      this.mobileOpen = false
      this.vehicleOpenId = null
      this.languageOpen = false
    },
    'siteConfig.logoUrl' () {
      this.logoFailed = false
    }
  },
  methods: {
    // 桌面端分类下拉：悬停展开，点击也能切换（键盘/触控板可用）
    openVehicleMenu (id) {
      this.vehicleOpenId = id
    },
    closeVehicleMenu () {
      this.vehicleOpenId = null
    },
    toggleVehicleMenu (id) {
      this.vehicleOpenId = this.vehicleOpenId === id ? null : id
    },
    // 语言切换：鼠标设备上悬停即展开；触屏设备不支持悬停，仍用点击切换
    supportsHover () {
      return typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
    },
    openLanguageMenu () {
      if (this.supportsHover()) this.languageOpen = true
    },
    closeLanguageMenu () {
      if (this.supportsHover()) this.languageOpen = false
    },
    toggleLanguageMenu () {
      // 支持悬停时点击只负责展开，避免"悬停已展开、点一下反而关掉"
      this.languageOpen = this.supportsHover() ? true : !this.languageOpen
    },
    async search () {
      if (!this.keyword) return
      this.searching = true
      try {
        const products = sortByOrder(listData(await getProducts({ keyword: this.keyword, lang: this.language })))
        const product = products[0]
        if (product && product.id) {
          this.$router.push(`/products/${product.id}`)
        } else {
          this.showSearchDialog(this.t('noResults'))
        }
      } catch (error) {
        this.notify(error.message || this.t('noResults'))
      } finally {
        this.searching = false
      }
    },
    productLink (product) {
      const id = product.id || this.slugify(product.name)
      return id ? `/products/${id}` : '/'
    },
    notify (message) {
      window.dispatchEvent(new CustomEvent('portal-message', { detail: message }))
    },
    showSearchDialog (message) {
      window.dispatchEvent(new CustomEvent('portal-dialog', { detail: message }))
    },
    slugify (value) {
      return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')
    },
    changeLanguage (language) {
      this.$store.dispatch('setLanguage', language)
      this.languageOpen = false
    }
  }
}
</script>
